import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
export function ApiAuthenticationPage() {
  return <DocPageLayout title="Authentication API" description="Authentication process to confirm the identity of the payer attempting to make a transaction." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Authentication'
  }]} sections={[{
    id: 'initiate-authentication',
    title: 'Initiate Authentication'
  }, {
    id: 'authenticate-payer',
    title: 'Authenticate Payer'
  }, {
    id: 'authentication-parameters',
    title: 'Authentication Parameters'
  }]} data-id="element-385">
      <p className="text-gray-700 mb-8" data-id="element-386">
        The Authentication API supports 3D Secure (3DS) authentication, which
        adds an extra layer of security by verifying the cardholder's identity
        with their issuing bank before completing a transaction.
      </p>

      <h2 id="initiate-authentication" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-387">
        Initiate Authentication
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-388">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-389">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-390">/merchant/</span>
        <span className="text-blue-600" data-id="element-391">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-392">/order/</span>
        <span className="text-blue-600" data-id="element-393">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-394">/transaction/</span>
        <span className="text-blue-600" data-id="element-395">{'{transactionId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-396">
        Initiates the 3DS authentication process. The gateway determines whether
        a frictionless or challenge flow is required based on risk assessment.
      </p>
      <CodeBlock language="JSON" title="Initiate Authentication Request" code={`{
  "apiOperation": "INITIATE_AUTHENTICATION",
  "authentication": {
    "acceptVersions": "3DS2",
    "channel": "PAYER_BROWSER",
    "purpose": "PAYMENT_TRANSACTION"
  },
  "correlationId": "test-correlation-123",
  "order": {
    "amount": "100.00",
    "currency": "USD"
  },
  "session": {
    "id": "SESSION0002abcdef1234567890"
  }
}`} data-id="element-397" />
      <CodeBlock language="JSON" title="Initiate Authentication Response" code={`{
  "authentication": {
    "3ds2": {
      "methodCompleted": false,
      "methodSupported": "SUPPORTED",
      "directoryServerId": "A000000004",
      "protocolVersion": "2.2.0",
      "requestorId": "example-requestor-id",
      "requestorName": "Example Merchant",
      "transactionId": "2bef1a60-0be0-4ce7-b9b2-example"
    },
    "version": "3DS2"
  },
  "order": {
    "id": "ORD-AUTH-001",
    "status": "AUTHENTICATION_INITIATED",
    "currency": "USD"
  },
  "response": {
    "gatewayCode": "AUTHENTICATION_IN_PROGRESS",
    "gatewayRecommendation": "PROCEED"
  },
  "result": "SUCCESS",
  "transaction": {
    "id": "TXN-AUTH-001"
  }
}`} data-id="element-398" />

      <h2 id="authenticate-payer" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-399">
        Authenticate Payer
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-400">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-401">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-402">/merchant/</span>
        <span className="text-blue-600" data-id="element-403">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-404">/order/</span>
        <span className="text-blue-600" data-id="element-405">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-406">/transaction/</span>
        <span className="text-blue-600" data-id="element-407">{'{transactionId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-408">
        Completes the authentication by submitting the payer's browser details
        and any challenge response data.
      </p>
      <CodeBlock language="JSON" title="Authenticate Payer Request" code={`{
  "apiOperation": "AUTHENTICATE_PAYER",
  "authentication": {
    "redirectResponseUrl": "https://your-site.com/3ds-callback"
  },
  "device": {
    "browser": "MOZILLA",
    "browserDetails": {
      "javaEnabled": false,
      "language": "en-US",
      "screenHeight": 1080,
      "screenWidth": 1920,
      "timeZone": -300
    }
  },
  "session": {
    "id": "SESSION0002abcdef1234567890"
  }
}`} data-id="element-409" />

      <h2 id="authentication-parameters" className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-410">
        Authentication Parameters
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-411">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-412">
          <thead data-id="element-413">
            <tr className="bg-gray-100" data-id="element-414">
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-415">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-416">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-417">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-418">
            <tr data-id="element-419">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-420">
                authentication.acceptVersions{' '}
                <span className="text-red-500" data-id="element-421">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-422">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-423">
                3DS version: 3DS1, 3DS2, or 3DS1,3DS2
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-424">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-425">
                authentication.channel <span className="text-red-500" data-id="element-426">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-427">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-428">
                PAYER_BROWSER or PAYER_APP
              </td>
            </tr>
            <tr data-id="element-429">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-430">
                authentication.purpose
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-431">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-432">
                PAYMENT_TRANSACTION, ADD_CARD, MAINTAIN_CARD
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-433">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-434">
                authentication.redirectResponseUrl
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-435">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-436">
                URL for 3DS challenge redirect callback
              </td>
            </tr>
            <tr data-id="element-437">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-438">
                device.browserDetails
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-439">Object</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-440">
                Browser fingerprint data for risk assessment
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <InfoBox type="warning" title="3DS2 Recommended" data-id="element-441">
        Always use 3DS2 for new integrations. 3DS1 is supported for backward
        compatibility but provides a less optimal user experience and may not
        satisfy regulatory requirements in some regions.
      </InfoBox>
    </DocPageLayout>;
}